import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Card,
    CardBody,
    Spinner,
    Center,
    Badge,
    useToast,
    Alert,
    AlertIcon,
    Icon,
    Grid,
    GridItem,
    Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useRouteConfig } from "./providers/RouteConfigProvider";
import { 
    HiAcademicCap, 
    HiCheckCircle, 
    HiBookOpen, 
    HiBadgeCheck,
    HiArrowRight,
    HiClock,
    HiUsers
} from "react-icons/hi";
import { COLORS } from "./styles/colors";

export const CertificateSelection = () => {
    const [fullCertifications, setFullCertifications] = useState([]);
    const [singleCourses, setSingleCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const toast = useToast();
    const { routes } = useRouteConfig();

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Fetch full certifications directly from full_certification table
            const { data: fullCerts, error: fullCertsError } = await supabase
                .from("full_certification")
                .select("*")
                .eq("is_active", true)
                .order("name");

            if (fullCertsError) {
                throw fullCertsError;
            }

            // Fetch individual courses from course_type table (certification is null or 'others')
            const { data: singleCerts, error: singleCertsError } = await supabase
                .from("course_type")
                .select("code, name, certification, school, price, course_duration")
                .or("certification.is.null,certification.eq.others")
                .order("name");

            if (singleCertsError) {
                throw singleCertsError;
            }

            setFullCertifications(fullCerts || []);
            setSingleCourses(singleCerts || []);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load courses. Please try again.");
            toast({
                title: "Error",
                description: "Failed to load courses.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        document.title = "Choose Your Learning Path - Sozcode";
        return () => {
            document.title = "Edthos Learning";
        };
    }, []);

    const handleFullCertificationSelect = (certificationId) => {
        navigate(`/register?type=certification&full_certification=${certificationId}`);
    };

    const handleSingleCourseSelect = (courseCode) => {
        navigate(`/registration?course=${courseCode}`);
    };

    if (loading) {
        return (
            <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
                <VStack spacing={4}>
                    <Spinner size="xl" color="gray.600" />
                    <Text color="gray.600">Loading courses...</Text>
                </VStack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
                <Alert status="error" borderRadius="md" maxW="md">
                    <AlertIcon />
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="6xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <VStack spacing={12} align="stretch">
                        {/* Header */}
                        <Box textAlign="center" py={8}>
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                            >
                                {/* <Text fontSize="5xl" mb={6}>
                                    🎓
                                </Text> */}
                                
                                <Heading 
                                    size="lg" 
                                    mb={4} 
                                    color="gray.800"
                                    fontWeight="700"
                                >
                                    Our Courses
                                </Heading>
                                
                            </motion.div>
                        </Box>

                        {/* Full Certifications Section */}
                        {fullCertifications.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4, duration: 0.6 }}
                            >
                                <Box mb={8}>
                                    <HStack spacing={3} mb={6} align="center">
                                        <Icon as={HiBadgeCheck} color="gray.700" boxSize={6} />
                                        <Heading size="md" color="gray.800">ERF programmes</Heading>
                                        <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                                            {fullCertifications.length} Available
                                        </Badge>
                                    </HStack>
                                    
                                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
                                        {fullCertifications.map((cert, index) => (
                                            <motion.div
                                                key={cert.code}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                            >
                                                <Card
                                                    bg="white"
                                                    borderRadius="xl"
                                                    boxShadow="lg"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _hover={{
                                                        boxShadow: "2xl",
                                                        transform: "translateY(-4px)",
                                                        transition: "all 0.3s ease-in-out"
                                                    }}
                                                    cursor="pointer"
                                                    onClick={() => handleFullCertificationSelect(cert.code)}
                                                    h="100%"
                                                >
                                                    <CardBody p={6}>
                                                        <VStack align="start" spacing={4} h="100%">
                                                            <HStack justify="space-between" width="100%">
                                                                <Box
                                                                    bg="blue.100"
                                                                    borderRadius="full"
                                                                    p={3}
                                                                    color="blue.600"
                                                                >
                                                                    <HiBadgeCheck size={24} />
                                                                </Box>
                                                                <Badge colorScheme="blue" variant="subtle" borderRadius="full">
                                                                    Diploma
                                                                </Badge>
                                                            </HStack>
                                                            
                                                            <VStack align="start" spacing={2} flex={1}>
                                                                <Heading size="xs" color="gray.800" lineHeight="1.2">
                                                                    {cert.name}
                                                                </Heading>
                                                                
                                                                <Text color="gray.600" fontSize="sm" noOfLines={3}>
                                                                    {cert.description || `Comprehensive ${cert.certification_type} program designed to enhance your professional skills.`}
                                                                </Text>
                                                                
                                                                <HStack spacing={4} fontSize="sm" color="gray.500">
                                                                    <HStack spacing={1}>
                                                                        <Icon as={HiBookOpen} boxSize={4} />
                                                                        <Text>{cert.total_modules} Modules</Text>
                                                                    </HStack>
                                                                    {cert.duration && (
                                                                        <HStack spacing={1}>
                                                                            <Icon as={HiClock} boxSize={4} />
                                                                            <Text>{cert.duration} months</Text>
                                                                        </HStack>
                                                                    )}
                                                                </HStack>
                                                                
                                                                {cert.cost && (
                                                                    <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                                                        ${cert.cost.toLocaleString()}
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                            
                                                            <Button
                                                                size="sm"
                                                                colorScheme="blue"
                                                                variant="outline"
                                                                rightIcon={<Icon as={HiArrowRight} />}
                                                                _hover={{
                                                                    bg: "blue.600",
                                                                    color: "white",
                                                                    borderColor: "blue.600"
                                                                }}
                                                                width="100%"
                                                            >
                                                                View Program
                                                            </Button>
                                                        </VStack>
                                                    </CardBody>
                                                </Card>
                                            </motion.div>
                                        ))}
                                    </Grid>
                                </Box>
                            </motion.div>
                        )}

                        {/* Divider */}
                        {fullCertifications.length > 0 && singleCourses.length > 0 && (
                            <Divider borderColor="gray.300" />
                        )}

                        {/* Single Courses Section */}
                        {/*
                        {singleCourses.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6, duration: 0.6 }}
                            >
                                <Box>
                                    <HStack spacing={3} mb={6} align="center">
                                        <Icon as={HiBookOpen} color="gray.700" boxSize={6} />
                                        <Heading size="md" color="gray.800">Individual Courses</Heading>
                                        <Badge colorScheme="green" variant="subtle" px={3} py={1} borderRadius="full">
                                            {singleCourses.length} Available
                                        </Badge>
                                    </HStack>
                                    
                                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
                                        {singleCourses.map((course, index) => (
                                            <motion.div
                                                key={course.code}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 * index, duration: 0.5 }}
                                            >
                                                <Card
                                                    bg="white"
                                                    borderRadius="xl"
                                                    boxShadow="lg"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _hover={{
                                                        boxShadow: "2xl",
                                                        transform: "translateY(-4px)",
                                                        transition: "all 0.3s ease-in-out"
                                                    }}
                                                    cursor="pointer"
                                                    onClick={() => handleSingleCourseSelect(course.code)}
                                                    h="100%"
                                                >
                                                    <CardBody p={6}>
                                                        <VStack align="start" spacing={4} h="100%">
                                                            <HStack justify="space-between" width="100%">
                                                                <Box
                                                                    bg="green.100"
                                                                    borderRadius="full"
                                                                    p={3}
                                                                    color="green.600"
                                                                >
                                                                    <HiBookOpen size={24} />
                                                                </Box>
                                                                <Badge colorScheme="green" variant="subtle" borderRadius="full">
                                                                    Course
                                                                </Badge>
                                                            </HStack>
                                                            
                                                            <VStack align="start" spacing={2} flex={1}>
                                                                <Heading size="xs" color="gray.800" lineHeight="1.2">
                                                                    {course.name}
                                                                </Heading>
                                                                
                                                                <Text color="gray.600" fontSize="sm" noOfLines={3}>
                                                                    Professional course designed to enhance your skills and knowledge in this specialized area.
                                                                </Text>
                                                                
                                                                <HStack spacing={4} fontSize="sm" color="gray.500">
                                                                    {course.course_duration && (
                                                                        <HStack spacing={1}>
                                                                            <Icon as={HiClock} boxSize={4} />
                                                                            <Text>{course.course_duration} hours</Text>
                                                                        </HStack>
                                                                    )}
                                                                    {course.school && (
                                                                        <HStack spacing={1}>
                                                                            <Icon as={HiUsers} boxSize={4} />
                                                                            <Text>{course.school}</Text>
                                                                        </HStack>
                                                                    )}
                                                                </HStack>
                                                                
                                                                {course.price && (
                                                                    <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                                                        ${course.price.toLocaleString()}
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                            
                                                            <Button
                                                                size="sm"
                                                                colorScheme="green"
                                                                variant="outline"
                                                                rightIcon={<Icon as={HiArrowRight} />}
                                                                _hover={{
                                                                    bg: "green.600",
                                                                    color: "white",
                                                                    borderColor: "green.600"
                                                                }}
                                                                width="100%"
                                                            >
                                                                View Course
                                                            </Button>
                                                        </VStack>
                                                    </CardBody>
                                                </Card>
                                            </motion.div>
                                        ))}
                                    </Grid>
                                </Box>
                            </motion.div>
                        )}
                        } */}

                        {/* No Courses Available */}
                        {fullCertifications.length === 0 && singleCourses.length === 0 && (
                            <Box textAlign="center" py={12}>
                                <Text fontSize="6xl" mb={4}>📚</Text>
                                <Heading size="lg" color="gray.800" mb={4}>
                                    No Courses Available
                                </Heading>
                                <Text color="gray.600" mb={6}>
                                    There are currently no courses available for registration.
                                </Text>
                                <Button
                                    onClick={() => navigate("/")}
                                    colorScheme="blue"
                                    variant="outline"
                                >
                                    Back to Home
                                </Button>
                            </Box>
                        )}

                        {/* Back Button */}
                        <Box textAlign="center" pt={8}>
                            <Button
                                onClick={() => navigate("/")}
                                variant="ghost"
                                color="gray.600"
                                _hover={{ color: "gray.800" }}
                                leftIcon={<Icon as={HiArrowRight} transform="rotate(180deg)" />}
                            >
                                Back to Home
                            </Button>
                        </Box>
                    </VStack>
                </motion.div>
            </Container>
        </Box>
    );
};
